import { Box, Button, Stack, Typography } from '@mui/material'
import DataTable from '../../../components/Tables/DataTable'
import DataTableHeaderWeb from '../../../components/Tables/DataTableHeaderWeb'
import { useState } from 'react'
import { useListPagination } from '../../../core/hooks/useListPagination'
import { TCollaborator } from '../../../core/types/Collaborators'
import { settingsCollaboratorsColumns } from './SettingsCollaboratorsColumns'
import SettingsCollaboratorRegister from './SettingsCollaboratorRegister'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import LoadingBackdrop from '../../../components/Loading/LoadingBackdrop'
import ConfirmDialog from '../../../components/Dialogs/ConfirmDialog'
import ErrorSnackbar from '../../../components/Snackbar/ErrorSnackbar'
import SuccessSnackbar from '../../../components/Snackbar/SuccessSnackbar'
import UserAccountPermissionController from '../../../core/controllers/UserAccountPermissionController'
import { TResponse } from '../../../core/types/ErrorResponse'

const SettingsCollaborators: React.FC = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [deleting, setDeleting] = useState<TCollaborator | null>(null)
  const [error, setError] = useState<string>('')
  const [deleted, setDeleted] = useState(false)
  const [updateStatus, setUpdateStatus] = useState<TCollaborator | null>(null)
  const [updated, setUpdated] = useState(false)

  const { data, loading, execute } = useListPagination<TCollaborator>({
    fetch: () =>
      UserAccountPermissionController.getCollaborators({
        page,
        rows: rowsPerPage,
        search,
      }),
  })

  const handleClose = () => {
    setOpen(false)
  }

  const handleChangeNewCollaborator = () => {
    setOpen(true)
  }

  const handleChangeDelete = async (value: TCollaborator) => {
    setDeleting(value)
  }

  const handleCloseDelete = () => {
    setDeleting(null)
  }

  const deleteCollaborator = async () => {
    if (!deleting) return

    try {
      setSending(true)
      const response = await UserAccountPermissionController.deleteCollaborator(
        {
          codeId: deleting.codeId,
        }
      )

      if (!response.success) {
        setError(response.error)
        return
      }

      setDeleted(true)
      execute()
      handleCloseDelete()
    } finally {
      setSending(false)
    }
  }

  const handleChangeStatus = async (value: TCollaborator) => {
    setUpdateStatus(value)
  }

  const handleCloseChangeStatus = () => {
    setUpdateStatus(null)
  }

  const updateStatusCollaborator = async () => {
    if (!updateStatus) return

    try {
      setSending(true)
      let res: TResponse<string> | null = null
      const request = { codeId: updateStatus.codeId }

      if (updateStatus.isEnabled)
        res = await UserAccountPermissionController.disableCollaborator(request)
      else
        res = await UserAccountPermissionController.enableCollaborator(request)

      if (!res.success) {
        setError(res.error)
        return
      }

      setUpdated(true)
      execute()
      handleCloseDelete()
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Stack direction="column" spacing={2} width="100%">
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            color="#343948"
            fontWeight={700}
            fontSize="16px"
            lineHeight="20px"
          >
            Meus Colaboradores
          </Typography>

          <Button
            variant="outlined"
            size="large"
            sx={{ textTransform: 'none' }}
            onClick={handleChangeNewCollaborator}
          >
            <Typography fontWeight={700} fontSize="14px" lineHeight="24px">
              + Novo Colaborador
            </Typography>
          </Button>
        </Stack>
        <Box>
          {data.items.length === 0 && search === '' && (
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              minHeight="20rem"
            >
              <PeopleOutlineIcon sx={{ color: '#9ea4ac', fontSize: '52px' }} />
              <Typography
                color="#9ea4ac"
                fontWeight={500}
                fontSize="20px"
                lineHeight="32px"
              >
                Cadastre seu primeiro colaborador
              </Typography>
              <Typography
                color="#9ea4ac"
                fontWeight={400}
                fontSize="16px"
                lineHeight="24px"
              >
                Permita que membros da sua equipe visualizem e editem seus
                produtos para te ajudar na gestão do dia a dia.
              </Typography>
              <Box height="1rem" />
              <Button
                variant="contained"
                size="large"
                sx={{ textTransform: 'none' }}
                onClick={handleChangeNewCollaborator}
              >
                Cadastrar Colaborador
              </Button>
            </Stack>
          )}
          {(data.items.length > 0 || search !== '') && (
            <DataTable
              columns={settingsCollaboratorsColumns({
                onDelete: handleChangeDelete,
                onUpdateStatus: handleChangeStatus,
              })}
              data={data}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              page={page}
              rowsPerPage={rowsPerPage}
              headerComponent={
                <DataTableHeaderWeb
                  labelItems="colaboradores"
                  totalItems={data.total}
                  setSearch={setSearch}
                  executeSearch={execute}
                  rowsPerPage={rowsPerPage}
                  handleChangeRowsPerPage={setRowsPerPage}
                />
              }
              hasNewCustomHeader
              headerBackgroundColor="transparent"
              enableBorders
            />
          )}
        </Box>
      </Stack>

      <SettingsCollaboratorRegister
        open={open}
        onClose={handleClose}
        onRefresh={execute}
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={handleCloseDelete}
        title="Excluir colaborador"
        message="Você tem certeza que deseja excluir este colaborador? Essa ação não pode ser desfeita."
        onYes={deleteCollaborator}
      />

      <ConfirmDialog
        open={!!updateStatus}
        onClose={handleCloseChangeStatus}
        title="Alteração colaborador"
        message={`Você tem certeza que deseja ${updateStatus?.isEnabled ? 'DESABILITAR' : 'HABILITAR'} este colaborador?`}
        onYes={updateStatusCollaborator}
      />

      <ErrorSnackbar open={error !== ''} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>

      <SuccessSnackbar open={deleted} onClose={() => setDeleted(false)}>
        Deletado com sucesso!
      </SuccessSnackbar>

      <SuccessSnackbar open={updated} onClose={() => setUpdated(false)}>
        Alterado com sucesso!
      </SuccessSnackbar>

      <LoadingBackdrop open={loading || sending} />
    </>
  )
}

export default SettingsCollaborators
